import React, { PropsWithChildren } from 'react';
import { Paper, PaperProps } from '@mui/material';

/**
 * Card Variants
 */
export const CARD_VARIANTS = {
  /** Border variant with grey border, no shadow */
  BORDER: 'border',
  /** Shadow variant with elevation */
  SHADOW: 'shadow',
  /** Flat variant with background only */
  FLAT: 'flat',
} as const;

export type CardVariant = typeof CARD_VARIANTS[keyof typeof CARD_VARIANTS];

/**
 * Card Props
 */
export interface CardProps extends Omit<PaperProps, 'variant'> {
  /** Visual variant of the card */
  variant?: CardVariant;
  /** Test ID for testing purposes */
  testId?: string;
}

/**
 * Card - Atomic container component built on MUI Paper
 * 
 * Provides a consistent card design for wrapping content sections
 * like price history, property appraisal, and listing details.
 * 
 * @example
 * ```tsx
 * <Card variant="border">
 *   <Content />
 * </Card>
 * 
 * <Card variant="shadow">
 *   <Content />
 * </Card>
 * ```
 */
export function Card({
  variant = CARD_VARIANTS.BORDER,
  testId,
  children,
  sx,
  ...paperProps
}: PropsWithChildren<CardProps>) {
  const variantStyles = {
    [CARD_VARIANTS.BORDER]: {
      border: '1px solid',
      borderColor: 'grey.300',
      boxShadow: 'none',
    },
    [CARD_VARIANTS.SHADOW]: {
      border: 'none',
    },
    [CARD_VARIANTS.FLAT]: {
      border: 'none',
      boxShadow: 'none',
    },
  };

  return (
    <Paper
      elevation={variant === CARD_VARIANTS.SHADOW ? 2 : 0}
      data-testid={testId}
      sx={{
        p: 2,
        borderRadius: 2,
        ...variantStyles[variant],
        ...sx,
      }}
      {...paperProps}
    >
      {children}
    </Paper>
  );
}

export default Card;
